import PropTypes from 'prop-types';
import classNames from 'classnames';

import { Button, Input, ModalV2 as Modal, Radio, Select, TextArea } from '@utilities';

import {
  DESCRIPTION_MAX_LENGTH,
  MAX_LENGTH_ERROR,
  OPTIONS,
  TEMPLATE_NAME_MAX_LENGTH,
} from './utilities/constants';
import styles from './_index.module.scss';

const NarrativesTemplatesModal = ({
  cancelButtonDataTestId,
  className = '',
  contentClassName = '',
  createOptionDataTestId,
  dataTestId,
  description = '',
  descriptionDataTestId,
  descriptionId,
  descriptionIsDisabled = false,
  error = null,
  fieldClassName = '',
  fieldControlClassName = '',
  formMode = 'edit',
  isActive = false,
  isLoading = false,
  isSubmitDisabled = false,
  isSubmitting = false,
  isTemplateNameRequired = false,
  nameInputDataTestId,
  onClose,
  onDescriptionChange,
  onOptionChange,
  onSubmit,
  onTemplateNameChange,
  onTemplateSelectChange,
  selectedOption = OPTIONS.CREATE,
  selectedTemplate = null,
  submitButtonDataTestId,
  subtext,
  subtextDataTestId,
  templateName = '',
  templateNameId,
  templateNameLabel = 'Template Name',
  templateNameMaxLength = TEMPLATE_NAME_MAX_LENGTH,
  templateOptions = [],
  templateSelectDataTestId,
  title,
  updateOptionDataTestId,
}) => {
  const isCreateUpdateMode = formMode === 'create-update';
  const isCreateSelected = selectedOption === OPTIONS.CREATE;
  const isUpdateSelected = selectedOption === OPTIONS.UPDATE;
  const shouldValidateTemplateNameMaxLength =
    typeof templateNameMaxLength === 'number' && templateNameMaxLength > 0;
  const isTemplateNameMaxLength =
    shouldValidateTemplateNameMaxLength && templateName.length >= templateNameMaxLength;
  const isDescriptionMaxLength = description.length >= DESCRIPTION_MAX_LENGTH;
  const disableSubmit =
    isSubmitDisabled ||
    isDescriptionMaxLength ||
    isTemplateNameMaxLength ||
    isLoading ||
    isSubmitting;

  const renderDescriptionField = ({ dataTestId: fieldDataTestId, isDisabled = false }) => (
    <div className={fieldClassName}>
      <TextArea
        aria-label="Template Description"
        className={classNames(fieldControlClassName, styles['narratives-templates-modal-textarea'])}
        data-testid={fieldDataTestId}
        error={isDescriptionMaxLength ? MAX_LENGTH_ERROR : ''}
        id={descriptionId}
        isDisabled={isDisabled}
        label="Description"
        maxLength={DESCRIPTION_MAX_LENGTH}
        onChange={onDescriptionChange}
        placeholder="Template description"
        rows={4}
        value={description}
      />
    </div>
  );

  const renderTemplateNameField = () => (
    <div className={fieldClassName}>
      <Input
        aria-label={templateNameLabel}
        className={fieldControlClassName}
        data-testid={nameInputDataTestId}
        error={isTemplateNameMaxLength ? MAX_LENGTH_ERROR : ''}
        id={templateNameId}
        isRequired={isTemplateNameRequired}
        label={templateNameLabel}
        maxLength={templateNameMaxLength || undefined}
        onChange={onTemplateNameChange}
        placeholder="Template name"
        value={templateName}
      />
    </div>
  );

  return (
    <Modal
      buttons={[
        <Button
          data-testid={cancelButtonDataTestId}
          key="cancel-btn"
          onClick={onClose}
          text="Cancel"
          variant="secondary"
        />,
        <Button
          data-testid={submitButtonDataTestId}
          isDisabled={disableSubmit}
          isLoading={isSubmitting}
          key="submit-btn"
          onClick={onSubmit}
          text="Save"
        />,
      ]}
      className={className}
      data-testid={dataTestId}
      isActive={isActive}
      onClose={onClose}
      title={title}
    >
      {error}
      {subtext ? <p data-testid={subtextDataTestId}>{subtext}</p> : null}
      {isCreateUpdateMode ? (
        <ul>
          <li data-testid={createOptionDataTestId}>
            <Radio
              isChecked={isCreateSelected}
              label="Create new template"
              name="save-as-template-option"
              onChange={onOptionChange}
              value={OPTIONS.CREATE}
            />
            {isCreateSelected ? (
              <div className={contentClassName}>
                {renderTemplateNameField()}
                {renderDescriptionField({ dataTestId: descriptionDataTestId })}
              </div>
            ) : null}
          </li>
          <li data-testid={updateOptionDataTestId}>
            <Radio
              isChecked={isUpdateSelected}
              label="Update existing template"
              name="save-as-template-option"
              onChange={onOptionChange}
              value={OPTIONS.UPDATE}
            />
            {isUpdateSelected ? (
              <div className={contentClassName}>
                <div className={fieldClassName}>
                  <Select
                    className={fieldControlClassName}
                    data-testid={templateSelectDataTestId}
                    isLoading={isLoading}
                    label={templateNameLabel}
                    onChange={onTemplateSelectChange}
                    options={templateOptions}
                    placeholder="Select template"
                    value={selectedTemplate}
                  />
                </div>
                {renderDescriptionField({
                  dataTestId: descriptionDataTestId,
                  isDisabled: descriptionIsDisabled,
                })}
              </div>
            ) : null}
          </li>
        </ul>
      ) : (
        <div className={contentClassName}>
          {renderTemplateNameField()}
          {renderDescriptionField({ dataTestId: descriptionDataTestId })}
        </div>
      )}
    </Modal>
  );
};

NarrativesTemplatesModal.propTypes = {
  cancelButtonDataTestId: PropTypes.string,
  className: PropTypes.string,
  contentClassName: PropTypes.string,
  createOptionDataTestId: PropTypes.string,
  dataTestId: PropTypes.string,
  description: PropTypes.string,
  descriptionDataTestId: PropTypes.string,
  descriptionId: PropTypes.string,
  descriptionIsDisabled: PropTypes.bool,
  error: PropTypes.node,
  fieldClassName: PropTypes.string,
  fieldControlClassName: PropTypes.string,
  formMode: PropTypes.oneOf(['create-update', 'edit']),
  isActive: PropTypes.bool,
  isLoading: PropTypes.bool,
  isSubmitDisabled: PropTypes.bool,
  isSubmitting: PropTypes.bool,
  isTemplateNameRequired: PropTypes.bool,
  nameInputDataTestId: PropTypes.string,
  onClose: PropTypes.func,
  onDescriptionChange: PropTypes.func,
  onOptionChange: PropTypes.func,
  onSubmit: PropTypes.func,
  onTemplateNameChange: PropTypes.func,
  onTemplateSelectChange: PropTypes.func,
  selectedOption: PropTypes.oneOf(Object.values(OPTIONS)),
  selectedTemplate: PropTypes.shape({
    description: PropTypes.string,
    label: PropTypes.string,
    name: PropTypes.string,
    value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  }),
  submitButtonDataTestId: PropTypes.string,
  subtext: PropTypes.string,
  subtextDataTestId: PropTypes.string,
  templateName: PropTypes.string,
  templateNameId: PropTypes.string,
  templateNameLabel: PropTypes.string,
  templateNameMaxLength: PropTypes.number,
  templateOptions: PropTypes.arrayOf(
    PropTypes.shape({
      description: PropTypes.string,
      label: PropTypes.string,
      name: PropTypes.string,
      value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    })
  ),
  templateSelectDataTestId: PropTypes.string,
  title: PropTypes.string,
  updateOptionDataTestId: PropTypes.string,
};

export default NarrativesTemplatesModal;
